/**
 * Session state for the prototype.
 *
 * ── Why a prototype needs a store at all ──
 *
 * A click-through of static screens tells you what the product looks like. It
 * does not tell you whether the product makes sense, because the interesting
 * question in a verification UI is what happens AFTER a decision: does the queue
 * count drop, does the control's verdict change, does the finding's timeline show
 * who decided and why, does the pack stop offering to export a control that is
 * still open? Those are the design questions worth reviewing, and they only
 * surface if state actually moves.
 *
 * So decisions here are real: recording one mutates the finding, appends a
 * timeline event attributed to the signed-in actor, updates every derived count
 * in the rail, and can flip a control's verdict. Reloading the page resets to the
 * fixture baseline — this is a prototype, not a database.
 *
 * ── The one rule the store enforces ──
 *
 * `recordDecision` REQUIRES a non-empty rationale. That is not form validation
 * for its own sake; it is the product's `silent_approve_rate` metric, which the
 * real engine treats as a hard gate rather than a target (see `data.ts` METRICS).
 * A prototype that lets you close a critical finding with an empty box teaches
 * exactly the wrong thing about what this product is for.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  CANDIDATES,
  CONTROLS,
  FINDINGS,
  ME,
  type Candidate,
  type Control,
  type DecisionEvent,
  type Finding,
  type ReviewCaseStatus,
  type ReviewDecisionKind,
  type VerificationStatus,
} from "./data";

/** The workspace owner an escalation is handed to. */
const OWNER_ACTOR_ID = "act_jl";

/* ── Decision → resulting state ──────────────────────────────────────────── */

/**
 * Where each decision leaves the finding.
 *
 * This table is the review state machine, and it is the reason the decision
 * buttons are not interchangeable. `request_evidence` deliberately does NOT close
 * anything — it returns the case to the owner and leaves the control UNKNOWN,
 * which is the honest outcome when the evidence cannot support a verdict either
 * way. `accept_exception` closes the finding but must never make the control read
 * as PASS; it becomes a stated limitation instead.
 */
const DECISION_RESULT: Record<
  ReviewDecisionKind,
  { status: Finding["status"]; caseStatus: ReviewCaseStatus | null; event: DecisionEvent["kind"] }
> = {
  accept_exception: {
    status: "accepted_exception",
    caseStatus: "exception_accepted",
    event: "exception_accepted",
  },
  // `caseStatus: null` = leave it where it is. Resetting to "open" wiped the
  // assignment context on a finding that stays in the queue by design — asking
  // for more evidence is not a fresh start, it is a step in the case.
  request_evidence: { status: "open", caseStatus: null, event: "request_evidence" },
  approve_remediation: { status: "remediated", caseStatus: "closed", event: "approve_remediation" },
  reject_remediation: {
    status: "open",
    caseStatus: "remediation_requested",
    event: "reject_remediation",
  },
  dismiss: { status: "dismissed", caseStatus: "closed", event: "dismiss" },
};

const DECISION_TITLE: Record<ReviewDecisionKind, string> = {
  accept_exception: "accepted an exception",
  request_evidence: "requested more evidence",
  approve_remediation: "approved the remediation",
  reject_remediation: "rejected the remediation",
  dismiss: "dismissed the finding",
};

/* ── Shape ───────────────────────────────────────────────────────────────── */

export interface Toast {
  id: number;
  text: string;
  /** `refused` renders a red ✕ instead of a green ✓ — see `Toasts` in `ui.tsx`.
   *  A refusal that looks like a success is worse than no feedback at all. */
  tone: "ok" | "refused";
}

export interface Store {
  findings: Finding[];
  controls: Control[];
  candidates: Candidate[];
  toasts: Toast[];
  /** Ids of findings decided in THIS session — the UI marks them so a reviewer
   *  can see what they just did without re-reading every timeline. */
  decidedThisSession: string[];
  recordDecision: (findingId: string, decision: ReviewDecisionKind, rationale: string) => void;
  assign: (findingId: string, actorId: string) => void;
  escalate: (findingId: string, rationale: string) => void;
  admitCandidate: (candidateId: string) => void;
  /** Simulates a re-verification run over one control. */
  reverify: (controlId: string) => void;
  toast: (text: string, tone?: "ok" | "refused") => void;
}

const StoreContext = createContext<Store | null>(null);

export function useStore(): Store {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore called outside StoreProvider");
  return ctx;
}

/* ── Provider ────────────────────────────────────────────────────────────── */

export function StoreProvider({ children }: { children: ReactNode }) {
  const [findings, setFindings] = useState<Finding[]>(FINDINGS);
  const [controls, setControls] = useState<Control[]>(CONTROLS);
  const [candidates, setCandidates] = useState<Candidate[]>(CANDIDATES);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [decidedThisSession, setDecided] = useState<string[]>([]);
  const nextId = useRef(1);

  const toast = useCallback((text: string, tone: "ok" | "refused" = "ok") => {
    const id = nextId.current++;
    setToasts((t) => [...t, { id, text, tone }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4_200);
  }, []);

  /** Append an event to a finding's timeline, attributed to the current actor. */
  const appendEvent = useCallback(
    (list: Finding[], findingId: string, event: Omit<DecisionEvent, "id" | "at" | "actorId">) =>
      list.map((f) =>
        f.id === findingId
          ? {
              ...f,
              timeline: [
                ...f.timeline,
                {
                  id: `ev_live_${nextId.current++}`,
                  at: new Date().toISOString(),
                  actorId: ME.id,
                  ...event,
                },
              ],
            }
          : f,
      ),
    [],
  );

  const recordDecision = useCallback(
    (findingId: string, decision: ReviewDecisionKind, rationale: string) => {
      const trimmed = rationale.trim();
      if (!trimmed) {
        // The hard gate. See the file header — silent approve rate is 0% by
        // construction, so there is no path through this function without a
        // rationale, not even a confirm dialog.
        toast("A rationale is required. No finding closes without one.", "refused");
        return;
      }
      const result = DECISION_RESULT[decision];
      setFindings((prev) => {
        const withEvent = appendEvent(prev, findingId, {
          kind: result.event,
          title: `${ME.name} ${DECISION_TITLE[decision]}`,
          note: trimmed,
        });
        return withEvent.map((f) =>
          f.id === findingId
            ? { ...f, status: result.status, caseStatus: result.caseStatus ?? f.caseStatus }
            : f,
        );
      });
      setDecided((d) => (d.includes(findingId) ? d : [...d, findingId]));

      // A closing decision can change what the control reads as — but only under
      // two conditions, and getting either wrong is the exact overstatement this
      // product exists to prevent:
      //
      //   1. Only `approve_remediation` may turn a control PASS. Accepting an
      //      exception explicitly must NOT: the evidence still does not show the
      //      control operating, so a receipt built on it would assert something
      //      false. An exception is a stated limitation, never a pass.
      //   2. The control must have NO OTHER open findings. Approving the bucket
      //      remediation on CC6.6 used to turn the whole control green while a
      //      critical, escalated finding on the same control was still open —
      //      which then dropped it out of `packBlockers` and offered it to the
      //      export dialog as "verified". One requirement passing is not the
      //      control passing.
      setControls((prevControls) => {
        if (decision !== "approve_remediation") return prevControls;
        const target = findings.find((f) => f.id === findingId);
        if (!target) return prevControls;
        const othersStillOpen = findings.some(
          (f) => f.controlId === target.controlId && f.id !== findingId && f.status === "open",
        );
        if (othersStillOpen) return prevControls;
        return prevControls.map((c) =>
          c.id === target.controlId ? { ...c, status: "PASS" as VerificationStatus } : c,
        );
      });
      toast(
        decision === "accept_exception"
          ? "Exception recorded. The control stays out of the passing set and the pack will carry it as a limitation."
          : `Decision recorded: ${DECISION_TITLE[decision]}.`,
      );
    },
    [appendEvent, findings, toast],
  );

  const assign = useCallback(
    (findingId: string, actorId: string) => {
      setFindings((prev) => {
        const withEvent = appendEvent(prev, findingId, {
          kind: "assigned",
          title: "Reassigned for review",
        });
        return withEvent.map((f) =>
          f.id === findingId ? { ...f, assignedActorId: actorId, caseStatus: "assigned" } : f,
        );
      });
      toast("Finding reassigned.");
    },
    [appendEvent, toast],
  );

  const escalate = useCallback(
    (findingId: string, rationale: string) => {
      const trimmed = rationale.trim();
      if (!trimmed) {
        toast("An escalation needs a reason — that is what the next person reads first.", "refused");
        return;
      }
      setFindings((prev) => {
        const withEvent = appendEvent(prev, findingId, {
          kind: "escalated",
          title: `${ME.name} escalated the disagreement`,
          note: trimmed,
        });
        // Reassigned to the workspace owner: escalating means handing the
        // decision over, so leaving it in your own queue misreports your workload
        // and hides the fact that someone else now owes an answer.
        return withEvent.map((f) =>
          f.id === findingId ? { ...f, caseStatus: "escalated", assignedActorId: OWNER_ACTOR_ID } : f,
        );
      });
      toast("Escalated to the workspace owner.");
    },
    [appendEvent, toast],
  );

  const admitCandidate = useCallback(
    (candidateId: string) => {
      const target = candidates.find((c) => c.id === candidateId);
      if (!target) return;
      if (!target.oracle || !target.oracleIsIndependent) {
        // Mirrors the engine: admission without an independent external oracle is
        // refused, not warned about. This is the product's central claim.
        toast("Cannot admit: this workflow has no independent oracle.", "refused");
        return;
      }
      setCandidates((prev) =>
        prev.map((c) =>
          c.id === candidateId
            ? {
                ...c,
                status: "admitted",
                // Without this the detail page's `status === "admitted" &&
                // admittedControlId` branch stayed false and kept offering an
                // enabled "Admit" button for a workflow already admitted.
                admittedControlId: c.admittedControlId ?? "ctl_cc81",
              }
            : c,
        ),
      );
      toast("Workflow admitted. It will be verified on the next run.");
    },
    [candidates, toast],
  );

  /**
   * Queue a re-verification for one control.
   *
   * The predicate that COUNTS the affected findings and the predicate that
   * MUTATES them have to be the same one. They were not: it counted open FAILs
   * and then rewrote every open finding on the control, so re-verifying CC6.6
   * silently moved an escalated critical finding out of `escalated` — losing a
   * disagreement that was deliberately parked for the workspace owner — and
   * reported a count that did not match what it changed.
   *
   * It also left no trace. The finding page's decision trail says "append-only,
   * every entry names who acted, when, and why", so a state change with no entry
   * makes that card a lie.
   */
  const reverify = useCallback(
    (controlId: string) => {
      const affects = (f: Finding) =>
        f.controlId === controlId && f.status === "open" && f.verificationStatus === "FAIL";
      const targets = findings.filter(affects);
      if (targets.length === 0) {
        toast("Nothing to re-verify on this control — no open failures.");
        return;
      }
      setFindings((prev) => {
        let next = prev;
        for (const t of targets) {
          next = appendEvent(next, t.id, {
            kind: "ready_for_reverification",
            title: `${ME.name} queued a re-verification run`,
            note: "Results land when the next evidence collection for this control completes.",
          });
        }
        return next.map((f) =>
          targets.some((t) => t.id === f.id) ? { ...f, caseStatus: "ready_for_reverification" } : f,
        );
      });
      toast(
        `Re-verification queued for ${targets.length} open failure${targets.length === 1 ? "" : "s"}. Results land when the next collection completes.`,
      );
    },
    [appendEvent, findings, toast],
  );

  const value = useMemo<Store>(
    () => ({
      findings,
      controls,
      candidates,
      toasts,
      decidedThisSession,
      recordDecision,
      assign,
      escalate,
      admitCandidate,
      reverify,
      toast,
    }),
    [
      findings,
      controls,
      candidates,
      toasts,
      decidedThisSession,
      recordDecision,
      assign,
      escalate,
      admitCandidate,
      reverify,
      toast,
    ],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

/* ── Derived selectors ───────────────────────────────────────────────────── */

/** Open findings — every count in the rail and on Overview reads this. */
export function openFindings(findings: Finding[]): Finding[] {
  return findings.filter((f) => f.status === "open");
}

/** Assigned to the signed-in reviewer and still open. */
export function myQueue(findings: Finding[]): Finding[] {
  return openFindings(findings).filter((f) => f.assignedActorId === ME.id);
}

/**
 * Severity-then-age ordering: critical first, and within a severity the oldest
 * first. This is the worklist order a reviewer actually wants, and it is why the
 * queue does not default to newest-first like an activity feed would.
 */
const SEV_RANK: Record<Control["consequence"], number> = { critical: 0, high: 1, medium: 2, low: 3 };

export function worstFirst(list: Finding[]): Finding[] {
  return [...list].sort((a, b) => {
    const s = SEV_RANK[a.severity] - SEV_RANK[b.severity];
    if (s !== 0) return s;
    return new Date(a.openedAt).getTime() - new Date(b.openedAt).getTime();
  });
}

/**
 * What stops a pack being exported today.
 *
 * Deliberately computed rather than stored: the Overview page's headline claim is
 * "you cannot hand this to an auditor yet, and here is the list", so the list has
 * to be derived from the same state the rest of the app shows, or the two will
 * disagree the moment someone records a decision.
 */
export function packBlockers(
  findings: Finding[],
  controls: Control[],
): { controlId: string; ref: string; reason: string }[] {
  const out: { controlId: string; ref: string; reason: string }[] = [];
  for (const c of controls) {
    if (c.status === "PASS") continue;
    const open = openFindings(findings).filter((f) => f.controlId === c.id);
    if (c.status === "NOT_RUN") {
      out.push({ controlId: c.id, ref: c.ref, reason: "never verified — no evidence source is mapped" });
    } else if (c.status === "FAIL") {
      out.push({
        controlId: c.id,
        ref: c.ref,
        reason: `FAIL with ${open.length} open finding${open.length === 1 ? "" : "s"}`,
      });
    } else if (c.status === "UNKNOWN") {
      out.push({
        controlId: c.id,
        ref: c.ref,
        reason: "UNKNOWN — evidence is insufficient to decide either way",
      });
    }
  }
  return out;
}
